import { debounce, debounceTime, Observable, Subject } from "rxjs";
import { s, Shape } from "../shapes";
import { MapAction } from "@kosmod/kit";

export namespace DBv2 {
  /**
   * An object specifying values (defined by selection "Keys" of the value)
   * to look up the full Object in a Table.
   */
  export type TableKey<Value, Keys extends (keyof Value)[]> = {
    [Key in (Keys[keyof Keys]) as (Key extends keyof Value ? Key : never)]:
      Key extends keyof Value ? Value[Key] : never
  }

  export type Table<Value, Keys extends (keyof Value)[]> = {
    name: string,

    create(value: Value): Promise<void>,
    read(key: TableKey<Value, Keys>): Promise<null | Value>,
    update(value: Value, key?: TableKey<Value, Keys> | null): Promise<void>,
    delete(key: TableKey<Value, Keys>): Promise<void>,
  }
  export type Index<Value, Keys extends (keyof Value)[]> = {
    name: string,

    list(key: TableKey<Value, Keys>): Promise<Value[]>,
  }

  export namespace Memory {
    export type Table<Value, Keys extends (keyof Value)[]> = DBv2.Table<Value, Keys> & {
      data: Map<string, Value>,
      events: Observable<TableKey<Value, Keys>>
    }
    export type Index<Value, Keys extends (keyof Value)[]> = DBv2.Index<Value, Keys> & {
      //indices: Map<string, TableKey<Value, Keys>[]>
    }

    export type Service = ReturnType<typeof createMemoryTableProvider>;
  }

  export type TableDef = {
    name: string,
    shape: Shape,
    primaryKeys: string[],
    indices: Record<string, { indexKeys: string[] }>
  }

  export type OfDB<T extends Record<string, TableDef>> = {
    [Key in keyof T]:
      Table<
        Shape.Type.Of<T[Key]['shape']>,
        T[Key]["primaryKeys"] extends (keyof Shape.Type.Of<T[Key]["shape"]>)[]
          ? T[Key]["primaryKeys"] : never
      > & {
        [IndexKey in keyof T[Key]['indices']]: Index<Shape.Type.Of<T[Key]['shape']>,
        T[Key]["indices"][IndexKey]['indexKeys'] extends (keyof Shape.Type.Of<T[Key]["shape"]>)[]
          ? T[Key]["indices"][IndexKey]['indexKeys'] : never
        >
      }
  }
}


const createMemoryTableProvider = () => {
  return {
    createTable: <const ValueShape extends Shape, const Keys extends (keyof Shape.Type.Of<ValueShape>)[]>(
      name: string,
      shape: ValueShape,
      keys: Keys
    ): DBv2.Memory.Table<Shape.Type.Of<ValueShape>, Keys> => {
      // shorthand types
      type Value = Shape.Type.Of<ValueShape>;
      type TableKey = DBv2.TableKey<Value, Keys>;

      const data = new Map<string, Value>()
      const events = new Subject<TableKey>();
      
      const flattenKey = (key: TableKey) => {
        return keys.map(keyName => key[keyName as keyof typeof key]).join('+')
      }
      const flattenValue = (value: Value) => {
        return keys.map(keyName => value[keyName]).join('+')
      }

      return {
        name,
        async create(value) {
          data.set(flattenValue(value), value);
          events.next(value as TableKey)
        },
        async read(key) {
          return data.get(flattenKey(key)) || null;
        },
        async update(value, key = null) {
          data.set(
            key ? flattenKey(key) : flattenValue(value),
            value
          );
          events.next(value as TableKey)
        },
        async delete(key) {
          data.delete(flattenKey(key));
          events.next(key)
        },
        data,
        events,
      }
    },
    createIndex<Value extends {}, Keys extends (keyof Value)[], FilterKeys extends (keyof Value)[]>(
      name: string,
      table: DBv2.Memory.Table<Value, Keys>,
      keys: FilterKeys
    ): DBv2.Memory.Index<Value, FilterKeys> {
      //const indices = new Map<string, DBv2.TableKey<Value, FilterKeys>[]>()
      type TableKey = DBv2.TableKey<Value, FilterKeys>;

      const flattenValue = (value: Value) => {
        return keys.map(keyName => value[keyName]).join('+')
      }
      const flattenKey = (key: TableKey) => {
        return keys.map(keyName => key[keyName as keyof typeof key]).join('+')
      }
      return {
        name,
        //indices,
        async list(key) {
          const flatKey = flattenKey(key);

          return [...table.data.values()]
            .filter(value => {
              const keyForValue = flattenValue(value);
              return keyForValue === flatKey
            })
        },
      }
    }
  }
}

const createFileTableProvider = (
  fs: typeof import('node:fs/promises'),
  path: typeof import('node:path'),
  storageDir: string,
): ReturnType<typeof createMemoryTableProvider> => {
  const runtimeImplementation = createMemoryTableProvider();

  return {
    ...runtimeImplementation,
    createTable(name, shape, keys) {
      const table = runtimeImplementation.createTable(name, shape, keys);

      const filename = `${storageDir}/${name}.json`;
      const dirname = path.dirname(filename)
      const dirReady = fs.mkdir(dirname, { recursive: true });

      const load = async () => {
        try {
          const data = await fs.readFile(`${storageDir}/${name}.json`, 'utf8')
          const rows = JSON.parse(data);
          for (const value of rows) {
            const key = keys.map(keyName => value[keyName]).join('+')
            table.data.set(key, value)
          }
        } catch { }
      }
      const save = async () => {
        await dirReady;
        await fs.writeFile(filename, JSON.stringify([...table.data.values()], null, 2), 'utf8')
      }

      load();
      table.events.pipe(debounceTime(1000)).subscribe(save);

      return table;
    },
  }
}

const createMemoryDBFromDef = <const T extends Record<string, DBv2.TableDef>>(provider: DBv2.Memory.Service, def: T): DBv2.OfDB<T> => {
  return Object.fromEntries(Object.entries(def).map(([name, tableDef]) => {
    const table = provider.createTable(tableDef.name, tableDef.shape, tableDef.primaryKeys as any) as any;

    const index = Object.fromEntries(Object.entries(tableDef.indices).map(([indexName, indexDef]) => {
      return [indexName, provider.createIndex([name, indexName].join('/'), table, indexDef.indexKeys as any)]
    }))
    return [name, { ...table, ...index }];
  })) as DBv2.OfDB<T>;
}

export const DBv2 = {
  memory: createMemoryTableProvider,
  simpleFile: createFileTableProvider,

  createMemoryDBFromDef,

  def<const T extends Record<string, DBv2.TableDef>>(def: T): T  {
    return def;
  }
};
